import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    effect,
    forwardRef,
    inject,
    input,
    untracked,
    viewChild,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { ScriptLoaderService } from '@core/services/script-loader.service';
import { WINDOW } from '@core/services/window.service';
import { environment } from '@environment/environment';

type TurnstileWindow = Window & {
    turnstile: Turnstile.Turnstile & { remove(widgetId: string): void };
    onloadTurnstileCallback: () => void;
};

@Component({
    selector: 'app-turnstile',
    imports: [ReactiveFormsModule],
    template: `
        @defer {
            <div
                #turnstileContainer
                [attr.data-sitekey]="siteKey"
                [attr.data-theme]="theme()"></div>
        } @placeholder {
            <div
                class="h-16 w-72 max-w-full animate-pulse rounded-sm bg-grey-150"></div>
        }
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TurnstileComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TurnstileComponent implements ControlValueAccessor {
    private readonly window = inject(WINDOW) as TurnstileWindow;
    private readonly scriptLoaderService = inject(ScriptLoaderService);
    private readonly destroyRef = inject(DestroyRef);

    theme = input<'light' | 'dark' | 'auto'>('light');

    readonly siteKey = environment.cloudflare.siteKey;
    readonly turnstileContainer = viewChild<ElementRef>('turnstileContainer');

    private widgetId: string | null = null;
    private onChange: (value: string | null) => void = () => {};
    private onTouched: () => void = () => {};

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.removeWidget();
        });

        // The container only exists once the @defer block has rendered,
        // which never happens during prerendering.
        effect(() => {
            if (this.turnstileContainer()) {
                untracked(() => this.initializeTurnstile());
            }
        });
    }

    private initializeTurnstile(): void {
        if (this.window.turnstile) {
            this.renderWidget();
            return;
        }

        this.window.onloadTurnstileCallback = () => {
            this.renderWidget();
        };

        this.scriptLoaderService
            .load(
                'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback'
            )
            .subscribe();
    }

    private renderWidget(): void {
        const container = this.turnstileContainer();
        if (!container?.nativeElement || !this.window.turnstile) {
            return;
        }

        this.widgetId = this.window.turnstile.render(container.nativeElement, {
            sitekey: this.siteKey,
            theme: this.theme(),
            callback: (token: string) => {
                this.onChange(token);
                this.onTouched();
            },
            'error-callback': () => {
                this.onChange(null);
                this.onTouched();
            },
            'expired-callback': () => {
                this.onChange(null);
            },
        });
    }

    private removeWidget(): void {
        if (this.widgetId && this.window.turnstile) {
            this.window.turnstile.remove(this.widgetId);
            this.widgetId = null;
        }
    }

    writeValue(value: string | null): void {
        // Turnstile tokens are managed by the widget itself,
        // so we don't need to do anything here
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
}
